import { useState, FormEvent } from "react";

import { tracking } from "@iwoca/frontend-tracking-library";
import { AlertBox, Button, Input, Label } from "@iwoca/orion";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import cn from "classnames";
import { useLocation } from "react-router";

import logger from "../../utils/logger";
import { redirectUser } from "../authentication";
import { completeNewPassword } from "../authentication-cognito";
import styles from "../CommonLogin.module.css";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import LoginContainer from "../LoginContainer";
import { getRedirectToLink } from "../LoginWithLink/getRedirectLink";

interface SetNewPasswordProps {
  email: string;
  temporaryPassword: string;
  onCancel: () => void;
}

/**
 * Validates password against Cognito password policy:
 * - Minimum 12 characters
 * - At least one lowercase letter
 * - At least one uppercase letter
 * - At least one number
 * - At least one symbol
 */
const validatePasswordStrength = (password: string): string | null => {
  if (password.length < 12) {
    return t`Password must be at least 12 characters long`;
  }
  if (!/[a-z]/.test(password)) {
    return t`Password must contain 1 or more lowercase characters (a-z).`;
  }
  if (!/[A-Z]/.test(password)) {
    return t`Password must contain 1 or more uppercase characters (A-Z).`;
  }
  if (!/[0-9]/.test(password)) {
    return t`Password must contain 1 or more numbers (0-9).`;
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    return t`Password must contain 1 or more symbols.`;
  }
  return null;
};

const SetNewPassword = ({
  email,
  temporaryPassword,
  onCancel,
}: SetNewPasswordProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setConfirmError(null);
    setGeneralError(null);

    // Validate password strength
    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) {
      setPasswordError(strengthError);
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setConfirmError(t`Passwords must be the same`);
      return;
    }

    setIsLoading(true);

    try {
      tracking.ampli.passwordSubmitted();
      const tokens = await completeNewPassword(
        email,
        temporaryPassword,
        newPassword,
      );
      tracking.ampli.loginSucceeded();

      redirectUser({
        redirectUrl: getRedirectToLink(),
        tokens,
      });
    } catch (error: any) {
      logger.error("Failed to set new password", {
        error: error?.message || "Unknown error",
        code: error?.code,
      });
      tracking.ampli.loginFailed();

      // Map Cognito errors to user-friendly messages
      if (error.code === "InvalidPasswordException") {
        setPasswordError(
          error.message || t`Password does not meet requirements`,
        );
      } else if (error.code === "NotAuthorizedException") {
        setGeneralError(
          t`Temporary password is invalid or has expired. Please request a new one.`,
        );
      } else if (error.code === "UserNotFoundException") {
        setGeneralError(t`There is no account with that email address`);
      } else {
        setGeneralError(
          t`Failed to set new password. Please try again or contact support.`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <form
        data-testid="set-new-password-form"
        className={styles.loginWithPassword}
        onSubmit={handleSubmit}
      >
        <h2 className={styles.heading}>
          <Trans>Set Your New Password</Trans>
        </h2>
        <p className={styles.subheading}>
          <Trans>
            You're logging in for the first time. Please create a new password
            for your account.
          </Trans>
        </p>

        <Label htmlFor="new-password-input" className={styles.loginLabel}>
          <Trans>Your new password</Trans>
        </Label>
        <Input
          className="fs-exclude"
          data-testid="new-password-input"
          type="password"
          placeholder="●●●●●●●●●●●●●●●●"
          id="new-password-input"
          value={newPassword}
          onChange={(event) => {
            setPasswordError(null);
            setNewPassword(event.target.value);
          }}
          onBlur={() => {
            const error = validatePasswordStrength(newPassword);
            if (error) {
              setPasswordError(error);
            }
          }}
        />
        {passwordError ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="password-error"
          >
            {passwordError}
          </AlertBox>
        ) : (
          <p className={styles.passwordRequirements}>
            <Trans>
              Password must be at least 12 characters and include uppercase,
              lowercase, numbers, and symbols
            </Trans>
          </p>
        )}

        <Label
          className={cn(styles.loginLabel, styles.loginLabelSpacer)}
          htmlFor="confirm-password-input"
        >
          <Trans>Confirm your new password</Trans>
        </Label>
        <Input
          className="fs-exclude"
          data-testid="confirm-password-input"
          type="password"
          placeholder="●●●●●●●●●●●●●●●●"
          id="confirm-password-input"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmError(null);
            setConfirmPassword(event.target.value);
          }}
          onBlur={() => {
            if (
              newPassword &&
              confirmPassword &&
              newPassword !== confirmPassword
            ) {
              setConfirmError(t`Passwords must be the same`);
            }
          }}
        />
        {confirmError ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="confirm-error"
          >
            {confirmError}
          </AlertBox>
        ) : null}

        {generalError ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="general-error"
          >
            {generalError}
          </AlertBox>
        ) : null}

        <div className={styles.buttonGroup}>
          <Button
            variant="primary"
            disabled={
              !newPassword ||
              !confirmPassword ||
              !!passwordError ||
              !!confirmError ||
              isLoading
            }
            type="submit"
            className={styles.submitButton}
          >
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Trans>Set password and log in</Trans>
            )}
          </Button>

          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={styles.cancelButton}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </form>
    </LoginContainer>
  );
};

export default SetNewPassword;
